/**
 * BlessingPopup — "A Blessing for Your Journey" modal.
 *
 * Globally mounted; listens for the `kalki:blessing-granted` custom event
 * emitted by ChatKrishna when a /kalki/respond or karma/sankalp completion
 * returns a `blessing` field with ok:true.
 *
 * STRICTLY separate from Surprise Credits — different visuals, different
 * copy, different ledger surface via /api/blessings/krishna.
 */
import React, { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";

const KIND_META = {
  gentle:            { title: "A Blessing for Your Journey",
                        subtitle: "Your thoughtful effort deserves recognition." },
  karma_blessing:    { title: "Karma Completed · Blessing Received",
                        subtitle: "You followed through. That matters." },
  sankalp_blessing:  { title: "Sankalp Completed · Blessing Received",
                        subtitle: "Discipline grows through small promises kept." },
};

export default function BlessingPopup() {
  const [event, setEvent] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      const d = e?.detail;
      if (!d || !d.ok) return;
      setEvent(d);
      // Also broadcast a credit refresh for any CreditsBadge listening.
      window.dispatchEvent(new CustomEvent("kalki:credits-updated",
        { detail: { balance: d.balance } }));
    };
    window.addEventListener("kalki:blessing-granted", handler);
    return () => window.removeEventListener("kalki:blessing-granted", handler);
  }, []);

  if (!event) return null;
  const meta = KIND_META[event.kind] || KIND_META.gentle;

  return (
    <div
      data-testid="blessing-popup"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 10001,
        background: "rgba(10,7,30,0.72)", backdropFilter: "blur(10px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) setEvent(null); }}
    >
      <div style={{
        width: "min(400px, 100%)",
        background: "linear-gradient(180deg,#1A0B3F 0%,#0E0527 100%)",
        borderRadius: 22,
        border: "1px solid rgba(179,132,255,0.26)",
        boxShadow: "0 32px 80px rgba(55,0,170,0.55)",
        padding: "26px 22px 22px",
        textAlign: "center",
        color: "#F6F1FF",
        position: "relative",
        fontFamily: "'Inter', sans-serif",
      }}>
        <button
          data-testid="blessing-popup-close"
          onClick={() => setEvent(null)}
          aria-label="Close"
          style={{
            position: "absolute", top: 12, right: 12,
            width: 32, height: 32, borderRadius: 999,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.08)",
            color: "#D7CBFF", cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}
        ><X size={16} /></button>
        <div style={{
          width: 72, height: 72, borderRadius: 999,
          margin: "6px auto 14px",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: "radial-gradient(circle at 30% 30%,#CFA8FF 0%,#8B5CF6 55%,#2B0075 100%)",
          boxShadow: "0 8px 32px rgba(139,92,246,0.55)",
        }}>
          <Sparkles size={30} color="#FFF" strokeWidth={1.6} />
        </div>
        <h3 style={{
          fontFamily: "'Fraunces', serif",
          fontSize: 22, fontWeight: 500,
          margin: "0 0 6px",
        }}>{meta.title}</h3>
        <p style={{ fontSize: 13, color: "#CFC2FA", margin: "0 0 14px", lineHeight: 1.5 }}>
          {meta.subtitle}
        </p>
        <div data-testid="blessing-popup-amount" style={{
          fontSize: 28, fontWeight: 600, letterSpacing: "0.03em",
          background: "linear-gradient(135deg,#CFA8FF,#FF9BD2)",
          WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
          marginBottom: 16,
        }}>+{event.credits} KALKI Credits</div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button
            data-testid="blessing-popup-continue"
            onClick={() => setEvent(null)}
            style={{
              padding: "12px 22px", borderRadius: 10,
              background: "linear-gradient(135deg,#7E3BFF,#B384FF)",
              color: "#FFF", fontSize: 12, fontWeight: 600, letterSpacing: "0.08em",
              border: "none", cursor: "pointer",
            }}
          >Continue My Journey</button>
          <button
            data-testid="blessing-popup-wallet"
            onClick={() => {
              setEvent(null);
              window.dispatchEvent(new CustomEvent("kalki:open-wallet"));
            }}
            style={{
              padding: "12px 20px", borderRadius: 10,
              background: "rgba(179,132,255,0.14)",
              color: "#F6F1FF", fontSize: 12, fontWeight: 500,
              border: "1px solid rgba(179,132,255,0.28)", cursor: "pointer",
            }}
          >View Wallet</button>
        </div>
      </div>
    </div>
  );
}
